import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Shield, Users, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';

const LandingPage = () => {
  const landingSchema = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": "https://www.dahlogsvane.dk/#organization",
      "name": "Dahl & Svane",
      "alternateName": "Dahl og Svane",
      "url": "https://www.dahlogsvane.dk/",
      "logo": "https://www.dahlogsvane.dk/og-image.png",
      "image": "https://www.dahlogsvane.dk/og-image.png",
      "description": "Dahl & Svane er jeres eksterne sikkerhedsafdeling. Vi leverer sikkerhedsrådgivning, beredskabsplaner, konflikthåndtering og beskæftigelsesrettet indsats. Baseret i Køge siden 2009.",
      "foundingDate": "2009",
      "telephone": "+45-70-26-22-63",
      "email": "info@dahlogsvane.dk",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Falkevej 30b",
        "addressLocality": "Køge",
        "postalCode": "4600",
        "addressCountry": "DK",
        "addressRegion": "Region Sjælland"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "55.4577",
        "longitude": "12.1821"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Denmark"
      },
      "sameAs": [
        "https://www.linkedin.com/company/dahl-&-svane/"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Sikkerheds- og beskæftigelsesydelser",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Security Management",
              "description": "Ekstern sikkerhedsafdeling med fysisk, digital, organisatorisk og beredskabsmæssig rådgivning"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Mennesker i Udvikling",
              "description": "Beskæftigelsesrettet indsats mellem kommune, borger og mentor"
            }
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://www.dahlogsvane.dk/#website",
      "url": "https://www.dahlogsvane.dk/",
      "name": "Dahl & Svane",
      "description": "Sikkerhed, beskæftigelse og tryghed",
      "publisher": {
        "@id": "https://www.dahlogsvane.dk/#organization"
      },
      "inLanguage": "da-DK"
    }
  ];

  return (
    <>
      <SEO
        title="Sikkerhed, beskæftigelse og tryghed"
        description="Dahl & Svane er jeres eksterne sikkerhedsafdeling. Vi leverer sikkerhedsrådgivning, beredskab, konflikthåndtering og beskæftigelsesrettet indsats. Baseret i Køge siden 2009."
        keywords="Dahl og Svane, sikkerhedsrådgivning Køge, ekstern sikkerhedsafdeling, beskæftigelsesrettet indsats, beredskabsplan, konflikthåndtering, NIS2 compliance Danmark"
        canonical="/"
        schema={landingSchema}
      />
      <div className="h-[100dvh] w-full flex flex-col md:flex-row overflow-hidden relative bg-charcoal font-sans group/layout">
      {/* Logo */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.8 }}
        className="absolute top-4 md:top-8 left-0 right-0 z-30 flex flex-col items-center pointer-events-none"
      >
        <div className="flex items-center text-white font-black tracking-[0.15em] text-lg md:text-2xl drop-shadow-md">
          <span>DAHL</span>
          <div className="flex flex-col items-center justify-center mx-2 md:mx-3 relative">
            <span>&amp;</span>
            <div className="absolute -bottom-[2px] md:-bottom-[3px] w-full h-[2px] md:h-[3px] bg-steel-blue"></div>
          </div>
          <span>SVANE</span>
        </div>
        <p className="mt-2 md:mt-3 text-[9px] md:text-xs text-gray-400 tracking-[0.15em] md:tracking-[0.2em] uppercase font-medium text-center px-4">
          Sikkerhed, beskæftigelse og tryghed
        </p>
      </motion.div>

      {/* Divider */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
        <div className="hidden md:block w-px h-[60vh] bg-white/10"></div>
        <div className="md:hidden h-px w-[60vw] bg-white/10"></div>
      </div>

      {/* Left Column - Security Management */}
      <div className="group relative w-full md:w-1/2 h-1/2 md:h-full overflow-hidden flex-1 transition-opacity duration-500 group-hover/layout:opacity-70 hover:!opacity-100">
        {/* Background Video */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 w-full h-full z-0"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster="/hero-poster.jpg"
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700 pointer-events-none"
          >
            <source src="/hero.mp4" type="video/mp4" />
          </video>
        </motion.div>
        
        {/* Animated Gradient Texture */}
        <motion.div 
          className="absolute inset-0 opacity-20 z-0 mix-blend-overlay"
          style={{
            background: "linear-gradient(-45deg, #1A1A1A, #2A2A2A, #1A1A1A, #3A3A3A)",
            backgroundSize: "400% 400%"
          }}
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 20, ease: "linear", repeat: Infinity }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/40 via-[#1A1A1A]/20 to-[#1A1A1A]/40 group-hover:bg-[#1A1A1A]/10 transition-colors duration-500 z-0"></div>

        {/* Content */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 pt-20 md:p-6 md:pt-6 text-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col items-center"
          >
            <Shield className="w-8 h-8 md:w-10 md:h-10 text-steel-blue mb-3 md:mb-6" />
            <h2 className="font-bold text-2xl sm:text-3xl md:text-5xl text-white tracking-tight">
              Security Management
            </h2>
            <p className="font-light text-sm sm:text-base md:text-lg text-gray-300 mt-2 md:mt-4 max-w-md mx-auto">
              Din eksterne sikkerheds-afdeling. Ét kontaktpunkt for al jeres sikkerhed: fysisk, digital, organisatorisk og beredskab.
            </p>
            <Link 
              to="/security" 
              className="mt-4 md:mt-8 px-6 py-3 md:px-10 md:py-4 bg-steel-blue hover:bg-[#4A6A89] text-white font-semibold text-sm md:text-base rounded-[8px] transition-colors duration-300 inline-flex items-center group/btn"
            >
              Udforsk
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 transform group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Right Column - Mennesker i Udvikling */}
      <div className="group relative w-full md:w-1/2 h-1/2 md:h-full overflow-hidden flex-1 bg-[#2A2A2A] transition-opacity duration-500 group-hover/layout:opacity-70 hover:!opacity-100">
        {/* Background Video */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 w-full h-full z-0"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster="/hero2-poster.jpg"
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700 pointer-events-none"
          >
            <source src="/hero2.mp4" type="video/mp4" />
          </video>
        </motion.div>

        {/* Animated Gradient Texture */}
        <motion.div 
          className="absolute inset-0 opacity-20 z-0 mix-blend-overlay"
          style={{
            background: "linear-gradient(45deg, #1A1A1A, #2A2A2A, #1A1A1A, #3A3A3A)",
            backgroundSize: "400% 400%"
          }}
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 20, ease: "linear", repeat: Infinity }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/40 via-[#1A1A1A]/20 to-[#1A1A1A]/40 group-hover:bg-[#1A1A1A]/10 transition-colors duration-500 z-0"></div>

        {/* Content */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 pb-20 md:p-6 md:pb-6 text-center">
          <motion.div
            initial={{ opacity: 0, x: 30 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col items-center"
          >
            <Users className="w-8 h-8 md:w-10 md:h-10 text-steel-blue mb-3 md:mb-6" />
            <h2 className="font-bold text-2xl sm:text-3xl md:text-5xl text-white tracking-tight">
              Mennesker i Udvikling
            </h2>
            <p className="font-light text-sm sm:text-base md:text-lg text-gray-300 mt-2 md:mt-4 max-w-md mx-auto">
              Beskæftigelsesrettet indsats. Vi bringer menneskers kompetencer i spil gennem målrettede forløb mellem kommune, borger og mentor.
            </p>
            <a 
              href="/menneskeriudvikling" 
              className="mt-4 md:mt-8 px-6 py-3 md:px-10 md:py-4 bg-steel-blue hover:bg-[#4A6A89] text-white font-semibold text-sm md:text-base rounded-[8px] transition-colors duration-300 inline-flex items-center group/btn"
            >
              Udforsk
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 transform group-hover/btn:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 0.8, delay: 0.7 }}
        className="absolute bottom-0 left-0 right-0 z-20 py-3 md:py-6 bg-black/50 backdrop-blur-md flex flex-col items-center gap-1 md:gap-2"
      >
        <p className="text-center text-[10px] md:text-sm text-gray-300 tracking-wide pointer-events-none px-2">
          Dahl & Svane  •  Falkevej 30b  •  4600 Køge  •  Tlf: 70 26 22 63  •  info@dahlogsvane.dk
        </p>
      </motion.div>
      </div>
    </>
  );
};

export default LandingPage;
